import { useCallback, useRef, useState } from 'react';

export type QueuedTask = {
  id: string;
  modelKey: string;
  payload: unknown;
  status: 'queued' | 'running' | 'completed' | 'failed';
  executor: () => Promise<unknown>;
};

export interface QueueStats {
  queued: number; // 等待中的任务数
  running: number; // 执行中的任务数
  completed: number; // 已完成的任务数（本次会话）
  failed: number; // 失败的任务数（本次会话）
  total: number; // 总任务数
}

type InternalQueuedTask = QueuedTask & {
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

type QueueState = {
  tasks: InternalQueuedTask[];
  running: number;
  maxConcurrent: number;
};

const DEFAULT_MAX_CONCURRENT = 20;

const createDeferred = () => {
  let resolve: (value: unknown) => void = () => {};
  let reject: (reason?: unknown) => void = () => {};
  const promise = new Promise<unknown>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const useTaskQueue = (options?: { defaultMaxConcurrent?: number }) => {
  const queuesRef = useRef<Map<string, QueueState>>(new Map());
  const [, setTick] = useState(0);

  const forceUpdate = useCallback(() => {
    setTick((prev) => (prev + 1) % Number.MAX_SAFE_INTEGER);
  }, []);

  const normalizeMaxConcurrent = useCallback(
    (maxConcurrent?: number) => {
      if (typeof maxConcurrent === 'number' && maxConcurrent > 0) {
        return maxConcurrent;
      }
      const fallback = options?.defaultMaxConcurrent ?? DEFAULT_MAX_CONCURRENT;
      return fallback > 0 ? fallback : DEFAULT_MAX_CONCURRENT;
    },
    [options?.defaultMaxConcurrent]
  );

  const ensureQueueState = useCallback(
    (modelKey: string, maxConcurrent?: number) => {
      const existing = queuesRef.current.get(modelKey);
      const normalizedMax = normalizeMaxConcurrent(maxConcurrent);
      if (existing) {
        if (existing.maxConcurrent !== normalizedMax) {
          existing.maxConcurrent = normalizedMax;
        }
        return existing;
      }
      const initial: QueueState = {
        tasks: [],
        running: 0,
        maxConcurrent: normalizedMax,
      };
      queuesRef.current.set(modelKey, initial);
      return initial;
    },
    [normalizeMaxConcurrent]
  );

  const runNext = useCallback(
    (modelKey: string) => {
      const queueState = ensureQueueState(modelKey);
      while (queueState.running < queueState.maxConcurrent) {
        const nextTask = queueState.tasks.find((task) => task.status === 'queued');
        if (!nextTask) {
          break;
        }
        nextTask.status = 'running';
        queueState.running += 1;
        forceUpdate();

        void nextTask
          .executor()
          .then((result) => {
            nextTask.status = 'completed';
            nextTask.resolve(result);
          })
          .catch((error) => {
            nextTask.status = 'failed';
            nextTask.reject(error);
          })
          .finally(() => {
            queueState.running = Math.max(0, queueState.running - 1);
            // 保留已完成/失败的任务用于统计，只移除超过 100 个的旧任务
            const completedOrFailed = queueState.tasks.filter(
              (task) => task.status === 'completed' || task.status === 'failed'
            );
            if (completedOrFailed.length > 100) {
              queueState.tasks = queueState.tasks
                .filter((task) => task.status === 'queued' || task.status === 'running')
                .concat(completedOrFailed.slice(-100));
            }
            forceUpdate();
            runNext(modelKey);
          });
      }
    },
    [ensureQueueState, forceUpdate]
  );

  const enqueue = useCallback(
    (
      modelKey: string,
      task: Omit<QueuedTask, 'status' | 'modelKey' | 'id'> & Partial<Pick<QueuedTask, 'id'>> | null,
      opts?: { maxConcurrent?: number }
    ) => {
      if (!task || typeof task.executor !== 'function') {
        console.error('[useTaskQueue] invalid task or missing executor', { modelKey, task });
        return null;
      }
      const queueState = ensureQueueState(modelKey, opts?.maxConcurrent);

      const deferred = createDeferred();
      const wrappedTask: InternalQueuedTask = {
        id: task.id ?? randomId(),
        modelKey,
        payload: task.payload ?? null,
        status: 'queued',
        executor: task.executor,
        promise: deferred.promise,
        resolve: deferred.resolve,
        reject: deferred.reject,
      };
      queueState.tasks.push(wrappedTask);
      forceUpdate();
      runNext(modelKey);
      return wrappedTask.promise;
    },
    [ensureQueueState, forceUpdate, runNext]
  );

  const getQueueLength = useCallback(
    (modelKey: string, opts?: { maxConcurrent?: number }) => {
      const queueState = ensureQueueState(modelKey, opts?.maxConcurrent);
      return queueState.tasks.filter((task) => task.status === 'queued').length;
    },
    [ensureQueueState]
  );

  const getQueueStats = useCallback(
    (modelKey: string, opts?: { maxConcurrent?: number }): QueueStats => {
      const queueState = ensureQueueState(modelKey, opts?.maxConcurrent);
      const tasks = queueState.tasks;
      return {
        queued: tasks.filter((t) => t.status === 'queued').length,
        running: queueState.running,
        completed: tasks.filter((t) => t.status === 'completed').length,
        failed: tasks.filter((t) => t.status === 'failed').length,
        total: tasks.length,
      };
    },
    [ensureQueueState]
  );

  /** @deprecated 队列不再有容量限制，此方法始终返回 false */
  const isQueueFull = useCallback(
    (_modelKey: string, _opts?: { maxConcurrent?: number }) => {
      return false;
    },
    []
  );

  return {
    enqueue,
    getQueueLength,
    isQueueFull,
    getQueueStats,
  };
};
